import { faExchangeAlt } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "antd";
import { playlistDistanceToTimestamp } from "components/util/GeometryUtilsV2";
import OBCSpinner from "components/util/OBC";
import {
    absoluteTimeLookup,
    asyncLoadImage,
    createPlaylistUrls,
    getUrlDataForFrame,
    videoFrameExtractor,
    videoTimestampExtractor,
} from "components/util/PlaylistUtils";
import { WithAspectRatio } from "components/util/WithAspectRatio";
import ZoomableImage from "components/ZoomableImage";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

const csrfTokenSelector = (state) => state.csrfToken;
const imageKeysSelector = (state) => state.playlist.data.video;
const baseUrlSelector = (state) => {
    const currentSession = state.sessions[state.playlist.data.routeID];
    const urls = createPlaylistUrls(currentSession, state.playlist.position.sourceIndex);
    return urls.base;
};

const offsetSelector = (state) => state.playlist.data.offsets;
const geometrySelector = (state) => state.playlist.data.route_locations_v2;
const currentDistanceSelector = (state) => state.playlist.position.playlistDistance;

const OlePatrolsStillsPreview = ({ sourceIndex, imageTitle, updatePossible = true, loadSourceIndex = () => {} }) => {
    const csrfToken = useSelector(csrfTokenSelector);
    const imageKeysFromStore = useSelector(imageKeysSelector);
    const baseUrl = useSelector(baseUrlSelector);
    const offsets = useSelector(offsetSelector);
    const geometry = useSelector(geometrySelector);
    const distance = useSelector(currentDistanceSelector);

    const [imageData, setImageData] = useState(null);
    const [loadedImageURL, setLoadedImageURL] = useState(null);

    const imageKeys = useMemo(() => {
        return _.get(imageKeysFromStore, sourceIndex, []);
    }, [imageKeysFromStore, sourceIndex]);

    const timestampWithOffset = useMemo(() => {
        const pipOffset = offsets ? (offsets[sourceIndex] ?? 0) : 0;

        const pipDistance = distance + pipOffset;

        return playlistDistanceToTimestamp(pipDistance, geometry);
    }, [offsets, sourceIndex, distance, geometry]);

    const frameIndex = useMemo(() => {
        let targetKeyIndex = absoluteTimeLookup(timestampWithOffset, imageKeys);
        targetKeyIndex = Math.min(Math.max(0, targetKeyIndex), imageKeys.length - 1);
        return targetKeyIndex;
    }, [imageKeys, timestampWithOffset]);

    const { imageURL, range } = useMemo(() => {
        let imageURL = null;
        let range = null;

        let targetKey = imageKeys[frameIndex];

        if (!targetKey) {
            return { imageURL: null, range: null };
        }

        const frameCount = videoFrameExtractor(targetKey);

        let offset = timestampWithOffset - videoTimestampExtractor(targetKey);
        let subIndex = 1;
        if (frameCount) {
            const duration = targetKey[2];
            const ratio = offset / duration;
            subIndex = ratio <= 0 ? 1 : ratio >= 1 ? frameCount : Math.floor(ratio * frameCount) + 1;
        }

        const urlFrameData = getUrlDataForFrame(imageKeys, frameIndex, subIndex, targetKey[0]);

        range = urlFrameData.range;
        imageURL = `${baseUrl}${urlFrameData.imageFile}`;

        if (csrfToken) {
            imageURL += `?csrf=${csrfToken}`;
        }

        return { imageURL, range };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageKeys, frameIndex, timestampWithOffset, baseUrl]);

    useEffect(() => {
        let active = true;

        if (!updatePossible || !imageURL) {
            return;
        }

        asyncLoadImage(imageURL, range)
            .then((imageData) => {
                if (active) {
                    setImageData(imageData);
                    setLoadedImageURL(imageURL);
                }
            })
            .catch(() => {
                //do nothing
            });

        return () => {
            active = false;
        };
    }, [imageURL, range, updatePossible]);

    return (
        <div className="OlePatrolsStillsPreview">
            <div className="OlePatrolsStillsPreviewTitle">{imageTitle}</div>
            <div className="OlePatrolsStillsPreviewImage">
                <div className="OlePatrolsStillsPreviewImageButtons">
                    <Tooltip title="Swap with main player">
                        <Button
                            size="small"
                            onClick={() => loadSourceIndex(sourceIndex)}>
                            <FontAwesomeIcon
                                className="OlePreviewSwapButtonIcon"
                                icon={faExchangeAlt}
                            />
                        </Button>
                    </Tooltip>
                </div>
                <div className={`OlePatrolsStillsPreviewOverlay ${!updatePossible || !loadedImageURL || loadedImageURL !== imageURL ? "active" : ""}`}>
                    {updatePossible && (!loadedImageURL || loadedImageURL !== imageURL) ? <OBCSpinner colorScheme="mono" /> : null}
                    {!updatePossible && <div>Pause player to load image...</div>}
                </div>
                <WithAspectRatio
                    ratio={1.7778} //16:9
                >
                    <ZoomableImage
                        zoom={1}
                        imgSrc={imageData}
                        destroyOnClose={true}
                        updateZoom={() => {}}
                    />
                </WithAspectRatio>
            </div>
        </div>
    );
};

export default OlePatrolsStillsPreview;
